import { useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { NodeSelection } from '@tiptap/pm/state';
import { GET_MEDIA_ASSETS } from '../../graphql';
import { Loader } from '../common/PageLoader';
import { SummarizeButton } from '../ui/rich-text-editor';
import {
  ActiveBlock,
  BodyImage,
  BodyVideo,
  Highlight,
  HtmlBlock,
  Subscript,
  Superscript,
  bodySignature,
  liftLoneMedia,
} from './bodyNodes';

/**
 * The article body, edited in place in the reading column ("TinyCMS UX
 * optimization review v10.zip", Prototype v7 → Article → Edit): a small
 * toolbar (B, I, H2, Link, List | Image, Embed), the body's blocks as boxes,
 * and + Paragraph. Images, the site's video players and anything the editor
 * can't edit (tables, styled boxes) are kept; if reading the body would still
 * lose something, it stays read-only instead.
 */

interface LibraryAsset {
  id: string;
  mime: string | null;
  originalFilename: string | null;
  renditions: { kind: string; url: string | null; isCurrent: boolean }[];
  video: {
    status: string;
    durationSeconds: number | null;
    posterUrl: string | null;
    frameUrl: string | null;
  } | null;
}

function imageUrl(a: LibraryAsset): string | null {
  const cur = a.renditions?.filter((r) => r.isCurrent) ?? [];
  for (const kind of ['canonical', 'web', 'original', 'thumb']) {
    const hit = cur.find((r) => r.kind === kind)?.url;
    if (hit) return hit;
  }
  return null;
}

const duration = (s: number | null) =>
  s == null ? '' : `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')}`;

type Panel = { kind: 'image' | 'embed'; pos: number | null } | null;

/** The selected image or video node, if the selection is one. */
function selectedMedia(editor: Editor, type: 'bodyImage' | 'bodyVideo') {
  const sel = editor.state.selection;
  return sel instanceof NodeSelection && sel.node.type.name === type
    ? { pos: sel.from, attrs: sel.node.attrs as Record<string, string | null> }
    : null;
}

export function ArticleBodyEditor({
  value,
  onChange,
  accountId,
  allowVideo,
  readOnlyHtml,
  storedForm,
  onRequestSummary,
}: {
  value: string;
  onChange: (html: string) => void;
  accountId?: string | null;
  /** Videos become players only through the TinyCMS save (TinyCMS articles). */
  allowVideo: boolean;
  /** What to show if the body can't be edited here (sanitized). */
  readOnlyHtml: string;
  /** How the save step rewrites the HTML, if it does, so the check covers it. */
  storedForm?: (html: string) => string;
  onRequestSummary?: (plainText: string) => Promise<string[]>;
}) {
  const [lossy, setLossy] = useState(false);
  // Set before anything can emit: once a body is lossy, nothing it says is saved.
  const lossyRef = useRef(false);
  const [panel, setPanel] = useState<Panel>(null);

  const editor = useEditor({
    // The toolbar's active states follow the selection.
    shouldRerenderOnTransaction: true,
    extensions: [
      StarterKit.configure({
        // An article's links keep whatever target/rel they had; new ones get none.
        link: { openOnClick: false, HTMLAttributes: { target: null, rel: null } },
      }),
      Placeholder.configure({ placeholder: 'Write the article…' }),
      BodyImage,
      BodyVideo,
      HtmlBlock,
      Superscript,
      Subscript,
      Highlight,
      ActiveBlock,
    ],
    content: liftLoneMedia(value || ''),
    editorProps: { attributes: { class: 'ink-be-doc', 'aria-label': 'Article body' } },
    onCreate: ({ editor: ed }) => {
      const stored = storedForm || ((html: string) => html);
      if (bodySignature(stored(value)) !== bodySignature(stored(ed.getHTML()))) {
        lossyRef.current = true;
        // false: no update event — one would hand the lossy HTML to autosave.
        ed.setEditable(false, false);
        setLossy(true);
      }
    },
    onUpdate: ({ editor: ed }) => {
      if (!lossyRef.current) onChange(ed.getHTML());
    },
  });

  if (!editor) return <Loader label="Loading editor" className="ink-detail-empty" />;

  if (lossy) {
    return (
      <div className="ink-be ink-edit-frame">
        <div className="ink-edit-frame-label">BODY · the full article, one block per paragraph</div>
        <p className="ink-be-lossy">
          This body has formatting the editor can’t keep yet, so it stays read-only here. To change
          the text, ask your agent.
        </p>
        <div className="ink-detail-body" dangerouslySetInnerHTML={{ __html: readOnlyHtml }} />
      </div>
    );
  }

  const btn = (label: string, active: boolean, run: () => void, title?: string) => (
    <button
      type="button"
      className="ink-be-tool"
      aria-pressed={active}
      title={title || label}
      onMouseDown={(e) => e.preventDefault()}
      onClick={run}
    >
      {label}
    </button>
  );

  const setLink = () => {
    const prev = (editor.getAttributes('link').href as string | undefined) || '';
    const url = window.prompt('Link address', prev || 'https://');
    if (url === null) return;
    const chain = editor.chain().focus().extendMarkRange('link');
    if (!url.trim()) chain.unsetLink().run();
    else chain.setLink({ href: url.trim() }).run();
  };

  const openPanel = (kind: 'image' | 'embed') => {
    const sel = selectedMedia(editor, kind === 'image' ? 'bodyImage' : 'bodyVideo');
    setPanel(panel?.kind === kind ? null : { kind, pos: sel?.pos ?? null });
  };

  const addParagraph = () => {
    const { doc } = editor.state;
    const last = doc.lastChild;
    if (last?.type.name === 'paragraph' && last.content.size === 0) {
      editor.chain().focus('end').run();
      return;
    }
    editor
      .chain()
      .insertContentAt(doc.content.size, { type: 'paragraph' })
      .focus('end')
      .scrollIntoView()
      .run();
  };

  return (
    <div className="ink-be ink-edit-frame">
      <div className="ink-edit-frame-label">BODY · the full article, one block per paragraph</div>
      <div className="ink-be-bar" role="toolbar" aria-label="Formatting">
        {btn('B', editor.isActive('bold'), () => editor.chain().focus().toggleBold().run(), 'Bold')}
        {btn(
          'I',
          editor.isActive('italic'),
          () => editor.chain().focus().toggleItalic().run(),
          'Italic'
        )}
        {btn(
          'H2',
          editor.isActive('heading', { level: 2 }),
          () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
          'Heading'
        )}
        {btn('Link', editor.isActive('link'), setLink)}
        {btn(
          'List',
          editor.isActive('bulletList'),
          () => editor.chain().focus().toggleBulletList().run(),
          'Bulleted list'
        )}
        <span className="ink-be-sep" aria-hidden="true" />
        {btn(
          'Image',
          panel?.kind === 'image' || editor.isActive('bodyImage'),
          () => openPanel('image'),
          editor.isActive('bodyImage') ? 'Edit this image' : 'Add an image'
        )}
        {allowVideo
          ? btn(
              'Embed',
              panel?.kind === 'embed' || editor.isActive('bodyVideo'),
              () => openPanel('embed'),
              editor.isActive('bodyVideo') ? 'Edit this video' : 'Add a video from your library'
            )
          : null}
        {onRequestSummary ? (
          <>
            <span className="ink-be-sep" aria-hidden="true" />
            <SummarizeButton editor={editor} onRequestSummary={onRequestSummary} />
          </>
        ) : null}
      </div>

      {panel ? (
        <MediaPanel
          key={`${panel.kind}:${panel.pos ?? 'new'}`}
          editor={editor}
          panel={panel}
          accountId={accountId}
          onClose={() => setPanel(null)}
        />
      ) : null}

      <EditorContent editor={editor} />
      <button type="button" className="ink-be-add" onClick={addParagraph}>
        + Paragraph
      </button>
    </div>
  );
}

/** Pick or edit an image (library or address) or a library video. */
function MediaPanel({
  editor,
  panel,
  accountId,
  onClose,
}: {
  editor: Editor;
  panel: NonNullable<Panel>;
  accountId?: string | null;
  onClose: () => void;
}) {
  const isImage = panel.kind === 'image';
  const type = isImage ? 'bodyImage' : 'bodyVideo';
  const existing =
    panel.pos != null ? (editor.state.doc.nodeAt(panel.pos)?.attrs as Record<string, any>) : null;
  const [src, setSrc] = useState<string>(existing?.src || '');
  const [text, setText] = useState<string>((isImage ? existing?.alt : existing?.title) || '');
  const [caption, setCaption] = useState<string>(existing?.caption || '');
  const [error, setError] = useState('');

  const { data, loading } = useQuery(GET_MEDIA_ASSETS, {
    variables: { accountId, limit: 60, offset: 0 },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const assets: LibraryAsset[] = data?.mediaAssets?.assets ?? [];
  const items = isImage
    ? assets
        .filter((a) => !a.video && (a.mime || 'image/').startsWith('image/'))
        .map((a) => ({
          id: a.id,
          value: imageUrl(a),
          thumb: imageUrl(a),
          label: a.originalFilename,
        }))
        .filter((i) => i.value)
    : assets
        .filter((a) => a.video?.status === 'ready')
        .map((a) => ({
          id: a.id,
          value: `video:${a.id}`,
          thumb: a.video?.posterUrl || a.video?.frameUrl || null,
          label: `${a.originalFilename || 'Video'}${a.video?.durationSeconds ? ` · ${duration(a.video.durationSeconds)}` : ''}`,
        }));

  const apply = () => {
    if (!src.trim()) {
      setError(isImage ? 'Pick an image or paste its address.' : 'Pick a video.');
      return;
    }
    if (isImage && !/^(https?:)?\/\//i.test(src.trim())) {
      setError('The image address must start with https://.');
      return;
    }
    if (!text.trim()) {
      setError(
        isImage
          ? 'Describe the image for screen readers.'
          : 'Describe what happens in the video. It is the player’s title for screen readers.'
      );
      return;
    }
    const attrs = isImage
      ? { src: src.trim(), alt: text.trim(), caption: caption.trim() || null }
      : { src: src.trim(), title: text.trim(), caption: caption.trim() || null };
    if (panel.pos != null) {
      editor.chain().focus().setNodeSelection(panel.pos).updateAttributes(type, attrs).run();
    } else {
      editor.chain().focus().insertContent({ type, attrs }).run();
    }
    onClose();
  };

  const remove = () => {
    if (panel.pos == null) return;
    editor.chain().focus().setNodeSelection(panel.pos).deleteSelection().run();
    onClose();
  };

  const editingVideo = !isImage && panel.pos != null;

  return (
    <div className="ink-be-panel" role="dialog" aria-label={isImage ? 'Image' : 'Video'}>
      <div className="ink-be-panel-head">
        <span>
          {panel.pos != null
            ? isImage
              ? 'Edit image'
              : 'Edit video'
            : isImage
              ? 'Add an image'
              : 'Add a video from your library'}
        </span>
        <button type="button" className="ink-link-btn" onClick={onClose}>
          Close
        </button>
      </div>

      {editingVideo ? null : (
        <>
          {loading && !assets.length ? (
            <Loader label="Loading your media" className="ink-form-hint" />
          ) : items.length ? (
            <div className="ink-be-grid">
              {items.map((i) => (
                <button
                  type="button"
                  key={i.id}
                  className="ink-be-thumb"
                  data-on={src === i.value ? 'true' : undefined}
                  title={i.label || ''}
                  onClick={() => setSrc(i.value as string)}
                >
                  {i.thumb ? <img src={i.thumb} alt="" /> : <span>video</span>}
                </button>
              ))}
            </div>
          ) : (
            <p className="ink-form-hint">
              {isImage
                ? 'No images in your media library yet. Paste an address below, or add images in Media.'
                : 'No ready videos in your media library. Upload one in Media; it can be placed once it finishes processing.'}
            </p>
          )}
          {isImage ? (
            <label className="ink-be-field">
              <span className="ink-form-label">Image address</span>
              <input
                className="ink-input"
                value={src}
                placeholder="https://…"
                onChange={(e) => setSrc(e.target.value)}
              />
            </label>
          ) : null}
        </>
      )}

      <label className="ink-be-field">
        <span className="ink-form-label">{isImage ? 'Alt text' : 'What happens in the video'}</span>
        <input
          className="ink-input"
          value={text}
          placeholder={
            isImage
              ? 'Describe the image for screen readers (required)'
              : 'For screen readers (required)'
          }
          onChange={(e) => setText(e.target.value)}
        />
      </label>
      <label className="ink-be-field">
        <span className="ink-form-label">Caption</span>
        <input
          className="ink-input"
          value={caption}
          placeholder="Optional, shown under it"
          onChange={(e) => setCaption(e.target.value)}
        />
      </label>
      {!isImage ? (
        <p className="ink-form-hint">
          Only your site’s own videos can be placed. The player appears once this saves.
        </p>
      ) : null}
      {error ? (
        <p className="ink-form-hint" style={{ color: 'var(--ink-red)' }} role="alert">
          {error}
        </p>
      ) : null}
      <div className="ink-be-panel-actions">
        <button type="button" className="ink-btn ink-btn-primary" onClick={apply}>
          {panel.pos != null ? 'Update' : 'Insert'}
        </button>
        <button type="button" className="ink-btn" onClick={onClose}>
          Cancel
        </button>
        {panel.pos != null ? (
          <button
            type="button"
            className="ink-link-btn"
            style={{ marginLeft: 'auto', color: 'var(--ink-red)' }}
            onClick={remove}
          >
            Remove
          </button>
        ) : null}
      </div>
    </div>
  );
}
